<script setup lang="ts">
/**
 * Per ADR-0047 §"Before/after pair". Renders two `simulated_call_transcript`
 * proposals (the `before` + `after` halves of one `pairId`) as a single
 * comparison card: two columns on `sm`+ viewports, stacked panels on
 * narrower ones. Each half shows the same body — caller persona, exercise
 * chips, transcript bubbles, rationale — under a "До" / "После" header so
 * the user reads them in parallel.
 */
import { useI18n } from 'vue-i18n';
import { Headphones } from 'lucide-vue-next';
import SimulatedCallBubbles from './SimulatedCallBubbles.vue';
import type { SimulatedCallTranscriptProposal } from '@/api/copilot';

const { t } = useI18n();

defineProps<{
  before: SimulatedCallTranscriptProposal;
  after: SimulatedCallTranscriptProposal;
}>();
</script>

<template>
  <div class="rounded-2xl border border-border bg-surface overflow-hidden">
    <header class="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-bg/40">
      <Headphones class="size-4 text-accent" />
      <span class="text-caption font-medium text-text">
        {{ t('components.copilot.simulatedCall.beforeLabel') }}
        <span class="text-text-muted px-1">·</span>
        {{ t('components.copilot.simulatedCall.afterLabel') }}
      </span>
    </header>
    <div class="grid gap-0 sm:grid-cols-2">
      <section class="p-4 border-b border-border sm:border-b-0 sm:border-r">
        <div class="text-[11px] font-medium uppercase tracking-wide text-text-muted mb-3">
          {{ t('components.copilot.simulatedCall.beforeLabel') }}
        </div>
        <div class="space-y-3">
          <p v-if="before.callerProfile" class="text-xs italic text-text-muted">
            {{ before.callerProfile }}
          </p>
          <div
            v-if="before.exercises && before.exercises.length > 0"
            class="flex flex-wrap gap-1.5"
          >
            <span
              v-for="(ex, i) in before.exercises"
              :key="i"
              class="inline-flex items-center rounded-full border border-border bg-bg px-2 py-0.5 text-[11px] text-text-muted"
            >
              {{ ex }}
            </span>
          </div>
          <SimulatedCallBubbles :exchange="before.exchange" />
          <p class="text-xs italic text-text-muted">{{ before.rationale }}</p>
        </div>
      </section>
      <section class="p-4">
        <div class="text-[11px] font-medium uppercase tracking-wide text-text-muted mb-3">
          {{ t('components.copilot.simulatedCall.afterLabel') }}
        </div>
        <div class="space-y-3">
          <p v-if="after.callerProfile" class="text-xs italic text-text-muted">
            {{ after.callerProfile }}
          </p>
          <div
            v-if="after.exercises && after.exercises.length > 0"
            class="flex flex-wrap gap-1.5"
          >
            <span
              v-for="(ex, i) in after.exercises"
              :key="i"
              class="inline-flex items-center rounded-full border border-border bg-bg px-2 py-0.5 text-[11px] text-text-muted"
            >
              {{ ex }}
            </span>
          </div>
          <SimulatedCallBubbles :exchange="after.exchange" />
          <p class="text-xs italic text-text-muted">{{ after.rationale }}</p>
        </div>
      </section>
    </div>
  </div>
</template>
